import { isHeicAsset } from '../../../heic';
import * as React from 'react';
import { FileImage, FileVideo2 } from 'lucide-react';
import { FilePicker } from '@shigo-ui/ui';

import { useObjectUrl } from '../../../../../../hooks/useObjectUrl';
import { dashboardT } from '../../../../../../lib/messages';
import { CreativeFileKind } from '../../../model';

export function CreativeFileField({
  kind,
  file,
  error,
  onFileChange,
}: {
  kind: CreativeFileKind;
  file: File | null;
  error?: string | undefined;
  onFileChange: (file: File | null) => void;
}) {
  const previewUrl = useObjectUrl(file);
  const [fileError, setFileError] = React.useState<string>();

  function changeFile(nextFile: File | null) {
    setFileError(undefined);
    if (nextFile && isHeicAsset(nextFile.name, nextFile.type)) {
      setFileError(dashboardT('creatives.heicUnsupported'));
      onFileChange(null);
      return;
    }
    onFileChange(nextFile);
  }

  return (
    <div className="border-input bg-card grid gap-4 rounded-md border p-4 sm:grid-cols-[160px_minmax(0,1fr)] sm:items-center">
      <div className="border-input bg-background flex aspect-video items-center justify-center overflow-hidden rounded-md border p-2">
        {previewUrl && kind === CreativeFileKind.Image ? (
          <img
            src={previewUrl}
            alt={dashboardT('creatives.filePreview')}
            className="h-full w-full object-contain"
          />
        ) : null}
        {previewUrl && kind === CreativeFileKind.Video ? (
          // User-selected local previews do not have a separate captions asset.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={previewUrl} controls className="h-full w-full object-contain" />
        ) : null}
        {!previewUrl && kind === CreativeFileKind.Image ? (
          <FileImage className="text-muted-foreground h-7 w-7" aria-hidden="true" />
        ) : null}
        {!previewUrl && kind === CreativeFileKind.Video ? (
          <FileVideo2 className="text-muted-foreground h-7 w-7" aria-hidden="true" />
        ) : null}
      </div>
      <FilePicker
        label={dashboardT('creatives.file')}
        required
        value={file}
        onFileChange={changeFile}
        accept={
          kind === CreativeFileKind.Image
            ? 'image/jpeg,image/png,image/gif,image/webp,image/avif,image/svg+xml,image/bmp'
            : 'video/*'
        }
        buttonLabel={dashboardT('creatives.chooseFile')}
        helperText={dashboardT(
          kind === CreativeFileKind.Image
            ? 'creatives.imageFileHelper'
            : 'creatives.videoFileHelper',
        )}
        helperTextPlacement="below"
        error={fileError ?? error}
      />
    </div>
  );
}
